if (!customElements.get('airplane-category-explorer')) {
  class AirplaneCategoryExplorer extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.triggers = [...this.querySelectorAll('[data-airplane-category-trigger]')];
      this.panels = [...this.querySelectorAll('[data-airplane-category-panel]')];
      this.scrollers = [...this.querySelectorAll('[data-airplane-subcategory-scroller]')];
      this.panelAnimations = new WeakMap();
      this.scrollFrames = new WeakMap();
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);

      this.addEventListener('click', this.handleClick);
      this.addEventListener('keydown', this.handleKeydown);
      document.addEventListener('shopify:block:select', this.handleBlockSelect);
      window.addEventListener('resize', this.handleResize);
      this.scrollers.forEach((scroller) => scroller.addEventListener('scroll', this.handleScroll, { passive: true }));
      requestAnimationFrame(() => this.syncAllScrollers());

      if (this.dataset.openFirst === 'true' && this.triggers[0]) {
        this.open(this.triggers[0].dataset.categoryId);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('keydown', this.handleKeydown);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      window.removeEventListener('resize', this.handleResize);
      this.scrollers.forEach((scroller) => {
        scroller.removeEventListener('scroll', this.handleScroll);
        cancelAnimationFrame(this.scrollFrames.get(scroller));
      });
      this.panels.forEach((panel) => this.panelAnimations.get(panel)?.cancel());
      this.initialized = false;
    }

    handleClick(event) {
      const scrollerControl = event.target.closest(
        '[data-airplane-subcategory-prev], [data-airplane-subcategory-next]'
      );
      if (scrollerControl && this.contains(scrollerControl)) {
        event.preventDefault();
        this.moveScroller(scrollerControl, scrollerControl.hasAttribute('data-airplane-subcategory-next') ? 1 : -1);
        return;
      }

      const trigger = event.target.closest('[data-airplane-category-trigger]');
      if (!trigger || !this.contains(trigger)) return;

      const isOpen = trigger.getAttribute('aria-expanded') === 'true';
      if (isOpen) {
        this.closeAll();
      } else {
        this.open(trigger.dataset.categoryId);
      }
    }

    handleKeydown(event) {
      const trigger = event.target.closest('[data-airplane-category-trigger]');
      if (!trigger || !this.contains(trigger)) return;

      const currentIndex = this.triggers.indexOf(trigger);
      let nextIndex = currentIndex;

      if (event.key === 'Escape' && trigger.getAttribute('aria-expanded') === 'true') {
        event.preventDefault();
        this.closeAll();
        trigger.focus();
        return;
      }

      if (event.key === 'ArrowLeft') nextIndex = (currentIndex + 1) % this.triggers.length;
      if (event.key === 'ArrowRight') nextIndex = (currentIndex - 1 + this.triggers.length) % this.triggers.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this.triggers.length - 1;

      if (nextIndex !== currentIndex) {
        event.preventDefault();
        this.triggers[nextIndex].focus();
      }
    }

    handleBlockSelect(event) {
      const trigger = this.triggers.find((item) => item.dataset.categoryId === event.detail.blockId);
      if (trigger) this.open(trigger.dataset.categoryId);
    }

    open(categoryId) {
      this.triggers.forEach((trigger) => {
        const active = trigger.dataset.categoryId === categoryId;
        trigger.setAttribute('aria-expanded', active ? 'true' : 'false');
      });

      this.panels.forEach((panel) => {
        const active = panel.dataset.categoryId === categoryId;
        this.animatePanel(panel, active);

        if (active) {
          const subcategoryScroller = panel.querySelector('.airplane-categories__subcategory-grid');
          if (subcategoryScroller) {
            requestAnimationFrame(() => {
              const firstCard = this.getScrollerCards(subcategoryScroller)[0];
              subcategoryScroller.dataset.activeIndex = '0';
              firstCard?.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'start' });
              this.syncScroller(subcategoryScroller);
            });
          }
        }
      });
    }

    closeAll() {
      this.triggers.forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'false');
      });

      this.panels.forEach((panel) => {
        this.animatePanel(panel, false);
      });
    }

    handleScroll(event) {
      const scroller = event.currentTarget;
      cancelAnimationFrame(this.scrollFrames.get(scroller));
      const frame = requestAnimationFrame(() => this.syncScroller(scroller, true));
      this.scrollFrames.set(scroller, frame);
    }

    handleResize() {
      this.syncAllScrollers();
    }

    getScrollerCards(scroller) {
      return [...scroller.querySelectorAll('.airplane-categories__subcategory')];
    }

    getVisibleCardCount(scroller, cards) {
      if (!cards.length) return 0;

      const gap = parseFloat(getComputedStyle(scroller).columnGap || getComputedStyle(scroller).gap) || 0;
      const cardWidth = cards[0].getBoundingClientRect().width;
      if (!scroller.clientWidth || !cardWidth) return cards.length;

      return Math.max(1, Math.floor((scroller.clientWidth + gap) / (cardWidth + gap)));
    }

    moveScroller(control, direction) {
      const panel = control.closest('[data-airplane-category-panel]');
      const scroller = panel?.querySelector('[data-airplane-subcategory-scroller]');
      if (!scroller) return;

      const cards = this.getScrollerCards(scroller);
      const visibleCards = this.getVisibleCardCount(scroller, cards);
      const maxStart = Math.max(0, cards.length - visibleCards);
      const currentIndex = Number(scroller.dataset.activeIndex || 0);
      const targetIndex = Math.min(maxStart, Math.max(0, currentIndex + direction * visibleCards));

      scroller.dataset.activeIndex = String(targetIndex);
      cards[targetIndex]?.scrollIntoView({
        behavior: this.reduceMotion.matches ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'start',
      });
      this.syncScroller(scroller);
    }

    syncAllScrollers() {
      this.scrollers.forEach((scroller) => this.syncScroller(scroller, true));
    }

    syncScroller(scroller, detectIndex = false) {
      const cards = this.getScrollerCards(scroller);
      const visibleCards = this.getVisibleCardCount(scroller, cards);
      const maxStart = Math.max(0, cards.length - visibleCards);
      let activeIndex = Math.min(maxStart, Number(scroller.dataset.activeIndex || 0));

      if (detectIndex && cards.length) {
        const scrollerRect = scroller.getBoundingClientRect();
        const isRtl = getComputedStyle(scroller).direction === 'rtl';
        const startEdge = isRtl ? scrollerRect.right : scrollerRect.left;
        let closestDistance = Number.POSITIVE_INFINITY;

        cards.forEach((card, index) => {
          const cardRect = card.getBoundingClientRect();
          const cardStart = isRtl ? cardRect.right : cardRect.left;
          const distance = Math.abs(cardStart - startEdge);
          if (distance < closestDistance) {
            closestDistance = distance;
            activeIndex = Math.min(index, maxStart);
          }
        });
      }

      scroller.dataset.activeIndex = String(activeIndex);

      const panel = scroller.closest('[data-airplane-category-panel]');
      const controls = panel?.querySelector('[data-airplane-subcategory-controls]');
      if (!controls) return;

      controls.hidden = maxStart === 0;
      const previous = controls.querySelector('[data-airplane-subcategory-prev]');
      const next = controls.querySelector('[data-airplane-subcategory-next]');
      if (previous) previous.disabled = activeIndex <= 0;
      if (next) next.disabled = activeIndex >= maxStart;
    }

    animatePanel(panel, shouldOpen) {
      const currentHeight = panel.hidden ? 0 : panel.getBoundingClientRect().height;
      const currentAnimation = this.panelAnimations.get(panel);
      currentAnimation?.cancel();
      this.panelAnimations.delete(panel);

      if (this.reduceMotion.matches || typeof panel.animate !== 'function') {
        panel.hidden = !shouldOpen;
        panel.removeAttribute('data-panel-state');
        panel.style.removeProperty('height');
        panel.style.removeProperty('opacity');
        panel.style.removeProperty('transform');
        return;
      }

      if (!shouldOpen && panel.hidden) return;

      panel.hidden = false;
      panel.dataset.panelState = shouldOpen ? 'opening' : 'closing';
      panel.style.height = 'auto';
      const targetHeight = shouldOpen ? panel.scrollHeight : 0;
      panel.style.height = `${currentHeight}px`;

      const animation = panel.animate(
        shouldOpen
          ? [
              {
                height: `${currentHeight}px`,
                opacity: currentHeight > 0 ? 1 : 0,
                transform: currentHeight > 0 ? 'translateY(0)' : 'translateY(-10px)',
              },
              { height: `${targetHeight}px`, opacity: 1, transform: 'translateY(0)' },
            ]
          : [
              { height: `${currentHeight}px`, opacity: 1, transform: 'translateY(0)' },
              { height: '0px', opacity: 0, transform: 'translateY(-8px)' },
            ],
        {
          duration: shouldOpen ? 380 : 260,
          easing: shouldOpen ? 'cubic-bezier(0.22, 1, 0.36, 1)' : 'cubic-bezier(0.4, 0, 1, 1)',
          fill: 'forwards',
        }
      );

      this.panelAnimations.set(panel, animation);
      animation.finished
        .then(() => {
          if (this.panelAnimations.get(panel) !== animation) return;

          this.panelAnimations.delete(panel);
          panel.hidden = !shouldOpen;
          panel.dataset.panelState = shouldOpen ? 'open' : 'closed';
          panel.style.removeProperty('height');
          panel.style.removeProperty('opacity');
          panel.style.removeProperty('transform');
          animation.cancel();
        })
        .catch(() => {});
    }
  }

  customElements.define('airplane-category-explorer', AirplaneCategoryExplorer);
}
